<template>
	<div class="column" :class="{'edit-mode':dashboards.editMode}" :style="{'width': dashboards.editMode ? '370px' : dashboards.getColumnWidth(index),'min-width': dashboards.editMode ? '370px' : dashboards.getColumnWidth(index)}">
		<div class="item-box" v-for="item in dashboards.getColumn(index)" :key="item.tag">

			<MyCalls v-if="item.type === PbxAppDashboardItemType.MYCALLS" :data="item as PbxAppDashboardItemMyCalls" />

			<QueuePanel v-if="item.type === PbxAppDashboardItemType.QUEUE" :data="item as PbxAppDashboardItemQueue" />

			<MultiQueuePanel v-if="item.type === PbxAppDashboardItemType.MULTIQUEUE" :data="item as PbxAppDashboardItemMultiQueue" />

			<ExtensionsPanel v-if="item.type === PbxAppDashboardItemType.EXTENSIONS" :data="item as PbxAppDashboardItemExtensions" />

			<WebPanel v-if="item.type === PbxAppDashboardItemType.WEB" :data="item as PbxAppDashboardItemWeb" :style="{'height': dashboards.editMode ? 'auto' : (item as PbxAppDashboardItemWeb).height+'px','width': dashboards.editMode ? 'auto' : (item as PbxAppDashboardItemWeb).width+'px'}" />

			<SwitchvarPanel v-if="item.type === PbxAppDashboardItemType.SWITCHVAR" :data="item as PbxAppDashboardItemSwitchVar" />

			<ShortcutsPanel v-if="item.type === PbxAppDashboardItemType.SHORTCUTS" :data="item as PbxAppDashboardItemShortcuts" />

		</div>
		<Button v-if="dashboards.editMode" @click="toggleAddItem($event)" :label="t('layout.addPanel')" size="small" fluid />
		<Menu ref="addItemMenu" :model="addItemMenuItems" :popup="true" />
		<div
			class="bottom-drop-target"
			:class="{'bottom-drop-target-over':isDragOver}"
			:style="{'height':dragOffset+'px'}"
			v-show="dashboards.dragItem"
			@dragenter="dragOver"
			@dragover="dragOver"
			@dragleave="dragLeave"
			@drop="dragDrop"
		>
			{{ t('layout.moveHere') }}
		</div>
	</div>
</template>

<script setup lang="ts">
import MyCalls from '@/components/dashboard/MyCallsPanel.vue'
import QueuePanel from '@/components/dashboard/QueuePanel.vue'
import MultiQueuePanel from '@/components/dashboard/MultiQueuePanel.vue'
import ExtensionsPanel from '@/components/dashboard/ExtensionsPanel.vue'
import WebPanel from '@/components/dashboard/WebPanel.vue'
import SwitchvarPanel from '@/components/dashboard/SwitchvarPanel.vue'
import ShortcutsPanel from '@/components/dashboard/ShortcutsPanel.vue'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import { type PbxAppDashboardItemExtensions, type PbxAppDashboardItemMyCalls, type PbxAppDashboardItemQueue, type PbxAppDashboardItemMultiQueue, type PbxAppDashboardItemShortcuts, type PbxAppDashboardItemSwitchVar, PbxAppDashboardItemType, type PbxAppDashboardItemWeb } from '@/TcxApi/PbxApp'
import Menu from 'primevue/menu'

const dashboards = useDashboardsStore()
const { t } = useI18n()

interface Props {
	index: number
}

const props = defineProps<Props>()

const isDragOver = ref(false)
const dragOffset = ref(31)
const leaveTimer = ref<ReturnType<typeof setTimeout> | undefined>(undefined)

const addItemMenu = ref<(InstanceType<typeof Menu> | null)>()
const addItemMenuItems = [
	{ label: t('layout.type.MYCALLS'), command: () => { dashboards.addItem(PbxAppDashboardItemType.MYCALLS, props.index) } },
	{ label: t('layout.type.QUEUE'), command: () => { dashboards.addItem(PbxAppDashboardItemType.QUEUE, props.index) } },
	{ label: t('layout.type.MULTIQUEUE'), command: () => { dashboards.addItem(PbxAppDashboardItemType.MULTIQUEUE, props.index) } },
	{ label: t('layout.type.EXTENSIONS'), command: () => { dashboards.addItem(PbxAppDashboardItemType.EXTENSIONS, props.index) } },
	{ label: t('layout.type.WEB'), command: () => { dashboards.addItem(PbxAppDashboardItemType.WEB, props.index) } },
	{ label: t('layout.type.SWITCHVAR'), command: () => { dashboards.addItem(PbxAppDashboardItemType.SWITCHVAR, props.index) } },
	{ label: t('layout.type.SHORTCUTS'), command: () => { dashboards.addItem(PbxAppDashboardItemType.SHORTCUTS, props.index) } },
]

function toggleAddItem(event: MouseEvent) {
	addItemMenu.value?.toggle(event)
}

function dragOver(event: DragEvent) {
	event.preventDefault()
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	isDragOver.value = true
	dashboards.dragTarget = { column: props.index, position: -1 }
	dragOffset.value = dashboards.dragItemHeight ?? 0
}

function dragLeave() {
	dashboards.dragTarget = undefined
	leaveTimer.value = setTimeout(() => {
		isDragOver.value = false
		dragOffset.value = 31
	}, 100)
}

function dragDrop(event: DragEvent) {
	event.preventDefault()
	if (leaveTimer.value) {
		clearTimeout(leaveTimer.value)
		leaveTimer.value = undefined
	}
	dragOffset.value = 31
	dashboards.dragDropped()
}
</script>

<style scoped>
	.column {
		margin-left: 5px;
		margin-right: 5px;
		border-radius: 5px;
		transition: 500ms;
	}
	.column.edit-mode {
		margin-top: 40px;
	}
	.item-box {
		margin-bottom: 10px;
	}
	.bottom-drop-target {
		border: 1px dashed var(--border-color-dragdrop);
		height: 31px;
		color: var(--border-color-dragdrop);
		font-size: var(--font-size-primary);
		text-align: center;
		border-radius: 5px 5px;
		padding-top: 3px;
		transition: 200ms;
	}
	.bottom-drop-target-over {
		background-color: var(--bg-color-dragdrop);
		color: var(--p-primary-background);
	}
</style>
